/**
 * PM - "A pedido" (Collection/Search).
 *
 * "A pedido" NO es un filtro nativo de Shopify (Search & Discovery ya tiene
 * sus filtros ocupados) -- por instrucción explícita, se resuelve
 * exclusivamente por tag + Liquid + JS, aceptando la limitación conocida:
 * `disponible-a-pedido` y `producto-a-pedido` son dos tags históricos que
 * Alejandro confirmó como EQUIVALENTES (2026-09-13) -- main-collection-
 * product-grid.liquid/main-search.liquid marcan data-pm-a-pedido="true" si
 * el producto tiene cualquiera de los dos; este archivo nunca lee tags
 * directamente, solo cuenta/oculta sobre ese data attribute ya resuelto.
 * opera sobre las cards que Shopify ya renderizó/paginó para la página
 * actual. product count y pagination siguen siendo los nativos de Shopify y
 * NUNCA se recalculan/reconstruyen server-side aquí -- solo se ajusta el
 * NÚMERO VISUAL del contador (#ProductCount) para que sea coherente con las
 * cards realmente visibles cuando "A pedido" está OFF.
 *
 * Estado en memoria, NO sessionStorage/localStorage: el requisito comercial
 * es que "A pedido" arranque SIEMPRE marcado en cada carga real de página
 * (F5, abrir otra colección, pestaña nueva), y solo se recuerde la elección
 * del usuario mientras la página actual siga viva (cambios de Marca/Tipo/
 * precio/sorting/pagination vía AJAX). sessionStorage/localStorage
 * sobreviven a un hard reload y romperían ese default -- por eso se usa
 * window.__pmAPedidoVisible, que un F5 real siempre reinicializa.
 *
 * Cada card marcada por Liquid con data-pm-a-pedido="true" (ver
 * main-collection-product-grid.liquid / main-search.liquid) se oculta o
 * muestra según window.__pmAPedidoVisible.
 *
 * <pm-a-pedido-scope> es un marcador vacío (hidden, sin huella visual) que
 * se coloca una vez junto al checkbox "A pedido" y una vez dentro del grid
 * de productos. Se usa como custom element -- no como <script> suelto --
 * porque tanto el grid como el sidebar de facets se reemplazan vía AJAX
 * (facets.js reconstruye su HTML por fetch/DOMParser); un <script> inyectado
 * así nunca vuelve a ejecutarse, pero connectedCallback() sí, cada vez que
 * el marcador se reinserta en el DOM -- mismo patrón que <price-range> y
 * <pm-availability-toggle> en este mismo theme.
 *
 * CONTADOR VISUAL (#ProductCount):
 * collection-actions.liquid y search-actions.liquid comparten el mismo id
 * "ProductCount" -- un único mecanismo cubre Collection y Search. Ese nodo
 * NUNCA se elimina/reinserta durante AJAX (assets/facets.js solo reemplaza
 * su innerHTML vía renderProductCount()), así que <pm-a-pedido-scope> no
 * sirve para detectar sus actualizaciones -- se usa un MutationObserver
 * sobre #ProductCount en su lugar.
 *
 * IMPORTANTE -- por qué NO se usa regex sobre el texto visible:
 * en Search, el texto incluye el término buscado tal cual ("Resultados de
 * la búsqueda para "SM58": 12 artículos") y PROMUSIC vende muchos modelos
 * con números (SM58, HD25, X32, K12...). Un regex \d+ sobre ese texto podría
 * capturar el número del término en vez del count real. Por eso
 * collection-actions.liquid/search-actions.liquid renderizan, dentro del
 * mismo innerHTML de #ProductCount, nodos ocultos inequívocos:
 *   [data-pm-server-count-value]   -- el count puro (un solo entero)
 *   [data-pm-count-singular-text]  -- la frase completa ya traducida con
 *                                     count:1 (incluye el término real)
 *   [data-pm-count-plural-before]  -- todo el texto ANTES del count, en la
 *                                     forma plural ya traducida (incluye el
 *                                     término real, intacto)
 *   [data-pm-count-plural-after]   -- todo el texto DESPUÉS del count, en
 *                                     la forma plural (ej. " artículos")
 * plural-before/after se obtienen en Liquid partiendo la traducción ya
 * renderizada con un count centinela (9999999, un valor que ningún término
 * de búsqueda real va a contener) vía el filtro `split` -- nunca tocando
 * results.terms. Esto reutiliza 100% las traducciones existentes
 * (templates.collection.results_with_count /
 * templates.search.results_with_count_and_term): no se hardcodea
 * "Producto/Productos" ni "artículo/artículos", y singular/plural sale de
 * pedirle a Shopify/Liquid esas dos formas reales (count:1 y count:sentinel),
 * no de una heurística gramatical manual.
 *
 * CONTADORES "Disponibles" / "A pedido" (fila DISPONIBILIDAD) -- SOLO SEARCH
 * desde la auditoría 2026-09-14 (ver más abajo):
 * pm-collection-filter-availability.liquid deja dos <span> vacíos y
 * `hidden` por defecto junto a cada checkbox (data-pm-available-count,
 * data-pm-a-pedido-count). Ambos se calculan en JS contando el DOM real
 * ([data-pm-product-card], [data-pm-product-card][data-pm-a-pedido="true"])
 * en vez de en Liquid, porque collection.products/search.results, cuando se
 * accede fuera del bloque {% paginate %} de la sección de grid, no
 * garantizan reflejar el mismo recorte de página (filtros + paginación) que
 * ya se renderizó -- el DOM real es la única fuente 100% fiable del
 * conjunto efectivamente cargado en esta página. "A pedido" cuenta SIN
 * excluir ocultos (a propósito: el count debe seguir mostrando el total de
 * A pedido del resultado aunque el checkbox esté OFF y las cards estén
 * hidden). "Disponibles" = total de cards MENOS A pedido -- ya no se lee de
 * filter.values (ese count de Shopify incluía A pedido dentro del mismo
 * número, mezclando dos filas que el usuario percibe como buckets
 * separados).
 *
 * PAGINATION -- por qué ambos se OCULTAN cuando no representan el universo
 * completo: el "products_per_page"/"results_per_page" real de esta tienda
 * (24-31 según la plantilla) es menor que muchas colecciones PROMUSIC
 * completas. Si la página actual no contiene TODOS los resultados
 * (comparado contra el count real de Shopify en
 * [data-pm-server-count-value], dentro de #ProductCount), un count parcial
 * de "Disponibles"/"A pedido" sería matemáticamente engañoso -- se prefiere
 * no mostrar nada a mostrar un número que no representa el total.
 *
 * COLLECTION (corregido 2026-09-14): refreshFacetCounts() (page-local, DOM)
 * ya NO se ejecuta en Collection -- pm-collection-filter-availability.liquid
 * resuelve ahí los 3 buckets (Disponible/A Pedido/Agotado) directamente en
 * Liquid, GLOBALES, con o sin paginación (ver ese snippet para la fórmula
 * exacta: Disponible = nativo Shopify menos A Pedido, Agotado deriva del
 * nativo, nunca de Disponible). Cada bucket expone su valor resuelto (o
 * vacío si aún pendiente de un fetch async) en data-pm-disponibles-global /
 * data-pm-a-pedido-global / data-pm-agotados-global. refreshFacetCounts()
 * se auto-desactiva comprobando si <pm-collection-counts-scope> llegó a
 * definirse (customElements.get(...)) -- solo se carga en
 * main-collection-product-grid.liquid, así que en Search esa comprobación
 * siempre da falso y el comportamiento page-local de este archivo sigue
 * intacto ahí.
 *
 * #ProductCount EN COLLECTION (corregido 2026-09-14): refreshGlobalProductCount()
 * reconstruye el número visible como SUMA de los buckets globales de arriba
 * -- Disponibles + (A Pedido si el checkbox está ON) + (Agotados si el
 * checkbox está ON) -- en vez de contar cards del DOM (fallaba con
 * paginación, ver auditoría 2026-09-13) o dejar fijo el texto server-side.
 * Si algún bucket necesario todavía está pendiente, no toca #ProductCount
 * -- se reintenta cuando dispare "pm:availability-counts-updated" (emitido
 * por pm-collection-counts.js / pm-collection-facets-prefetch.js al
 * resolver un bucket async). #ProductCount EN SEARCH sigue exactamente
 * igual que antes: refreshProductCount() usa serverVisibleText/
 * areFacetCountsReliable()/countVisibleProductCards(), sin cambios.
 */
(function () {
  if (window.__pmAPedidoVisible === undefined) {
    window.__pmAPedidoVisible = true;
  }

  var countObserver = null;
  var serverVisibleText = null;
  var singularText = null;
  var pluralBefore = null;
  var pluralAfter = null;

  function isVisible() {
    return window.__pmAPedidoVisible !== false;
  }

  function setVisible(visible) {
    window.__pmAPedidoVisible = visible;
  }

  function applyVisibility() {
    var visible = isVisible();
    document.querySelectorAll('[data-pm-a-pedido="true"]').forEach(function (card) {
      card.hidden = !visible;
    });
  }

  function countVisibleProductCards() {
    return document.querySelectorAll('[data-pm-product-card]:not([hidden])').length;
  }

  function countTotalProductCards() {
    return document.querySelectorAll('[data-pm-product-card]').length;
  }

  function countAPedidoCards() {
    return document.querySelectorAll('[data-pm-product-card][data-pm-a-pedido="true"]').length;
  }

  // Lee el count real de Shopify ya expuesto para el contador principal
  // (ver captureServerCountNodes). Es la única fuente de "total universo".
  function getServerCount() {
    var node = document.querySelector('#ProductCount [data-pm-server-count-value]');
    if (!node) {
      return null;
    }
    var value = parseInt(node.textContent.trim(), 10);
    return isNaN(value) ? null : value;
  }

  // "Fiable" significa: la página actual contiene TODOS los resultados del
  // universo (sin count real disponible, se asume NO fiable -- fail closed,
  // preferimos ocultar antes que arriesgar un número engañoso).
  function areFacetCountsReliable() {
    var serverCount = getServerCount();
    if (serverCount === null) {
      return false;
    }
    return countTotalProductCards() >= serverCount;
  }

  // Disponibles/A pedido (page-local, fallback SOLO para Search):
  // ocultos por completo si la página actual no representa el universo
  // completo (pagination). Se recalculan en cada reconexión del grid (AJAX
  // de facets/sorting/pagination).
  //
  // En Collection esto ya NO corre: assets/pm-collection-counts.js resuelve
  // los 3 buckets (Disponible/A Pedido/Agotado) con conteos GLOBALES vía
  // fetch async al índice completo de la colección (auditoría 2026-09-14),
  // reemplazando esta aproximación page-local. Detectamos "estamos en
  // Collection" comprobando si ese custom element llegó a definirse (el
  // script solo se carga en main-collection-product-grid.liquid) -- si existe,
  // cedemos para no pisar sus números con los de esta página.
  function refreshFacetCounts() {
    if (customElements.get('pm-collection-counts-scope')) {
      return;
    }

    var availableNode = document.querySelector('[data-pm-available-count]');
    var aPedidoNode = document.querySelector('[data-pm-a-pedido-count]');

    if (!areFacetCountsReliable()) {
      if (availableNode) {
        availableNode.hidden = true;
      }
      if (aPedidoNode) {
        aPedidoNode.hidden = true;
      }
      return;
    }

    var aPedidoCount = countAPedidoCards();
    var availableCount = countTotalProductCards() - aPedidoCount;

    if (availableNode) {
      availableNode.textContent = availableCount;
      availableNode.hidden = false;
    }
    if (aPedidoNode) {
      aPedidoNode.textContent = aPedidoCount;
      aPedidoNode.hidden = false;
    }
  }

  // Lee los nodos inequívocos renderizados por Liquid dentro de
  // #ProductCount. Se relee en cada mutación real porque facets.js
  // reconstruye ese innerHTML completo en cada respuesta AJAX.
  function captureServerCountNodes() {
    var container = document.getElementById('ProductCount');
    if (!container) {
      serverVisibleText = null;
      return null;
    }

    var textNode = container.querySelector('[data-pm-count-text]');
    var singularNode = container.querySelector('[data-pm-count-singular-text]');
    var beforeNode = container.querySelector('[data-pm-count-plural-before]');
    var afterNode = container.querySelector('[data-pm-count-plural-after]');

    serverVisibleText = textNode ? textNode.textContent : null;
    singularText = singularNode ? singularNode.textContent : null;
    pluralBefore = beforeNode ? beforeNode.textContent : null;
    pluralAfter = afterNode ? afterNode.textContent : null;

    return container;
  }

  function buildCorrectedText(newCount) {
    if (newCount === 1 && singularText !== null) {
      return singularText;
    }
    if (pluralBefore !== null && pluralAfter !== null) {
      return pluralBefore + newCount + pluralAfter;
    }
    return null;
  }

  function isCollectionContext() {
    return Boolean(customElements.get('pm-collection-counts-scope'));
  }

  // Lee un bucket GLOBAL ya resuelto por pm-collection-filter-availability.
  // liquid (o completado luego por pm-collection-counts.js/pm-collection-
  // facets-prefetch.js). null significa "todavía no se conoce" (pendiente) --
  // nunca 0 por defecto, para no restar de más mientras se espera.
  function parseGlobalCount(selector, datasetKey) {
    var node = document.querySelector(selector);
    if (!node) {
      return null;
    }
    var raw = node.dataset[datasetKey];
    if (raw === undefined || raw === '') {
      return null;
    }
    var value = parseInt(raw, 10);
    return isNaN(value) ? null : value;
  }

  function getDisponiblesGlobal() {
    return parseGlobalCount('[data-pm-disponibles-global]', 'pmDisponiblesGlobal');
  }

  function getAPedidoGlobal() {
    return parseGlobalCount('[data-pm-a-pedido-global]', 'pmAPedidoGlobal');
  }

  function getAgotadosGlobal() {
    return parseGlobalCount('[data-pm-agotados-global]', 'pmAgotadosGlobal');
  }

  function isAgotadosOn() {
    var toggle = document.querySelector('[data-pm-agotados-toggle]');
    return Boolean(toggle && toggle.checked);
  }

  // PM - hotfix 2026-09-14: #ProductCount en Collection ya NO se corrige
  // contando cards del DOM (eso fallaba con paginación -- ver auditoría
  // 2026-09-13) ni se deja fijo en el texto server-side cuando "A pedido"
  // está OFF. Ahora se reconstruye como SUMA DE BUCKETS GLOBALES
  // (Disponibles/A Pedido/Agotados, ver pm-collection-filter-availability.
  // liquid), que ya son correctos con o sin paginación. Si algún bucket
  // necesario todavía está pendiente (fetch async sin resolver), esta
  // función no toca #ProductCount -- se vuelve a llamar cuando dispare
  // "pm:availability-counts-updated".
  function refreshGlobalProductCount() {
    var container = document.getElementById('ProductCount');
    var textNode = container ? container.querySelector('[data-pm-count-text]') : null;
    if (!container || !textNode) {
      return;
    }

    var disponibles = getDisponiblesGlobal();
    if (disponibles === null) {
      return;
    }

    var total = disponibles;

    if (isVisible()) {
      var aPedido = getAPedidoGlobal();
      if (aPedido === null) {
        return;
      }
      total += aPedido;
    }

    if (isAgotadosOn()) {
      var agotados = getAgotadosGlobal();
      if (agotados === null) {
        return;
      }
      total += agotados;
    }

    var nextText = buildCorrectedText(total);
    if (nextText === null || textNode.textContent === nextText) {
      return;
    }

    if (countObserver) {
      countObserver.disconnect();
    }
    textNode.textContent = nextText;
    if (countObserver) {
      countObserver.observe(container, { childList: true, characterData: true, subtree: true });
    }
  }

  // Solo se corrige el texto visible con el conteo local de cards si la
  // página actual representa el universo completo (areFacetCountsReliable).
  // Si hay paginación, mostrar un número derivado de una sola página como si
  // fuera el total sería incorrecto (ver auditoria 2026-09-13): #ProductCount
  // debe permanecer siempre en el valor real de Shopify en ese caso, sin
  // importar el estado de "A pedido". SOLO Search -- en Collection,
  // refreshGlobalProductCount() (arriba) es quien manda, ver el branch de
  // abajo.
  function refreshProductCount() {
    if (isCollectionContext()) {
      refreshGlobalProductCount();
      return;
    }

    var container = document.getElementById('ProductCount');
    var textNode = container ? container.querySelector('[data-pm-count-text]') : null;

    if (!container || !textNode || serverVisibleText === null) {
      return;
    }

    var nextText = serverVisibleText;
    if (!isVisible() && areFacetCountsReliable()) {
      nextText = buildCorrectedText(countVisibleProductCards());
    }

    if (nextText === null || textNode.textContent === nextText) {
      return;
    }

    if (countObserver) {
      countObserver.disconnect();
    }
    textNode.textContent = nextText;
    if (countObserver) {
      countObserver.observe(container, { childList: true, characterData: true, subtree: true });
    }
  }

  function observeServerCount() {
    var container = document.getElementById('ProductCount');
    if (!container || countObserver) {
      return;
    }

    countObserver = new MutationObserver(function () {
      captureServerCountNodes();
      refreshProductCount();
    });
    countObserver.observe(container, { childList: true, characterData: true, subtree: true });
  }

  function resetToDefault() {
    setVisible(true);
    applyVisibility();
    refreshProductCount();
  }

  // "Borrar filtros"/"Clear all" (facet-remove) intercepta el click para su
  // propio flujo AJAX; este listener adicional no interfiere (no llama
  // preventDefault) y simplemente restaura el baseline "A pedido" = ON.
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-pm-clear-all]')) {
      resetToDefault();
    }
  });

  // PM - hotfix 2026-09-14: cuando un bucket global pendiente (A Pedido >50,
  // o Agotados con otros filtros activos) termina de resolverse de forma
  // async (pm-collection-counts.js / pm-collection-facets-prefetch.js),
  // ambos disparan este evento en document para que #ProductCount se
  // recalcule sin esperar ninguna otra interacción del usuario.
  document.addEventListener('pm:availability-counts-updated', function () {
    refreshProductCount();
  });

  captureServerCountNodes();
  observeServerCount();

  class PmAPedidoScope extends HTMLElement {
    connectedCallback() {
      applyVisibility();
      refreshProductCount();
      refreshFacetCounts();
      observeServerCount();

      this.toggle = this.parentElement
        ? this.parentElement.querySelector('[data-pm-a-pedido-toggle]')
        : null;

      if (!this.toggle) {
        return;
      }

      this.toggle.checked = isVisible();
      this.onToggleChange = this.onToggleChange.bind(this);
      this.toggle.addEventListener('change', this.onToggleChange);
    }

    disconnectedCallback() {
      if (this.toggle) {
        this.toggle.removeEventListener('change', this.onToggleChange);
      }
    }

    onToggleChange() {
      setVisible(this.toggle.checked);
      applyVisibility();
      refreshProductCount();
    }
  }

  if (!customElements.get('pm-a-pedido-scope')) {
    customElements.define('pm-a-pedido-scope', PmAPedidoScope);
  }
})();
